import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Anchor } from '@mantine/core';
import { IconEye, IconFileCheck } from '@tabler/icons-react';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { formatDateTime } from '#/utils/DateUtil.ts';
import { DOCUMENT_TYPE_LABEL } from '../constants/documentTypes.ts';
import type { ReviewTabValue } from '../constants/reviewTabs.ts';
import type { AccountDocumentSummaryItem } from '../model.ts';

type DocumentReviewTableProps = {
  tab: ReviewTabValue;
  records: AccountDocumentSummaryItem[];
  totalRecords: number;
  page: number;
  pageSize: number;
  fetching: boolean;
  onPageChange: (page: number) => void;
};

const TITLE_STYLE = { color: 'gray', fontWeight: 'normal' };

const documentTypeColumn: DataTableColumn<AccountDocumentSummaryItem> = {
  title: '駕駛資料類型',
  titleStyle: TITLE_STYLE,
  accessor: 'documentType',
  render: ({ documentType }) => {
    const documentTypeColors = {
      VEHICLE_REGISTRATION: 'orange',
      DRIVING_LICENSE: 'grape',
      INSURANCE_POLICY: 'teal',
    };

    return (
      <Badge
        size="lg"
        variant="light"
        color={documentTypeColors[documentType] ?? 'gray'}
      >
        {DOCUMENT_TYPE_LABEL[documentType]}
      </Badge>
    );
  },
};

const emailColumn: DataTableColumn<AccountDocumentSummaryItem> = {
  title: 'Email',
  titleStyle: TITLE_STYLE,
  accessor: 'account.email',
  render: ({ account }) => (
    <Anchor
      component={Link}
      to={`/driver-management/drivers/${account.id}`}
      size="sm"
      underline="always"
    >
      {account.email}
    </Anchor>
  ),
};

const uploadDateTimeColumn: DataTableColumn<AccountDocumentSummaryItem> = {
  title: '上傳時間',
  titleStyle: TITLE_STYLE,
  accessor: 'uploadDateTime',
  render: ({ uploadDateTime }) => formatDateTime(uploadDateTime),
};

const reviewDateTimeColumn: DataTableColumn<AccountDocumentSummaryItem> = {
  title: '審核時間',
  titleStyle: TITLE_STYLE,
  accessor: 'reviewDateTime',
  render: ({ reviewDateTime }) =>
    reviewDateTime ? formatDateTime(reviewDateTime) : '-',
};

const rejectReasonColumn: DataTableColumn<AccountDocumentSummaryItem> = {
  title: '備註',
  titleStyle: TITLE_STYLE,
  accessor: 'rejectReason',
  render: ({ rejectReason }) => rejectReason || '-',
};

const ReviewActionButton = ({
  id,
  label,
  icon,
}: {
  id: string;
  label: string;
  icon: ReactNode;
}) => {
  const [searchParams] = useSearchParams();

  return (
    <Button
      size="compact-sm"
      variant="light"
      leftSection={icon}
      component={Link}
      to={{ pathname: id, search: searchParams.toString() }}
    >
      {label}
    </Button>
  );
};

const buildActionColumn = (
  label: string,
  icon: ReactNode,
): DataTableColumn<AccountDocumentSummaryItem> => ({
  title: '操作',
  titleStyle: TITLE_STYLE,
  accessor: 'id',
  render: ({ id }) => <ReviewActionButton id={id} label={label} icon={icon} />,
});

const COLUMNS_BY_TAB: Record<
  ReviewTabValue,
  DataTableColumn<AccountDocumentSummaryItem>[]
> = {
  pending: [
    documentTypeColumn,
    emailColumn,
    uploadDateTimeColumn,
    buildActionColumn('審核', <IconFileCheck size={16} />),
  ],
  rejected: [
    documentTypeColumn,
    emailColumn,
    uploadDateTimeColumn,
    reviewDateTimeColumn,
    rejectReasonColumn,
    buildActionColumn('檢視', <IconEye size={16} />),
  ],
  approved: [
    documentTypeColumn,
    emailColumn,
    uploadDateTimeColumn,
    reviewDateTimeColumn,
    buildActionColumn('檢視', <IconEye size={16} />),
  ],
  submitted: [
    documentTypeColumn,
    emailColumn,
    uploadDateTimeColumn,
    buildActionColumn('檢視', <IconEye size={16} />),
  ],
};

const DocumentReviewTable = ({
  tab,
  records,
  totalRecords,
  page,
  pageSize,
  fetching,
  onPageChange,
}: DocumentReviewTableProps) => (
  <DataTable
    idAccessor="id"
    highlightOnHover
    verticalSpacing="sm"
    minHeight={`calc(100vh - 250px)`}
    columns={COLUMNS_BY_TAB[tab]}
    records={records}
    fetching={fetching}
    totalRecords={totalRecords}
    recordsPerPage={pageSize}
    page={page}
    paginationText={({ from, to, totalRecords }) =>
      `顯示 ${from} - ${to} 筆，共 ${totalRecords} 筆`
    }
    onPageChange={onPageChange}
    noRecordsText="查無資料"
    styles={{ pagination: { color: 'gray' } }}
  />
);

export { DocumentReviewTable };
